// Modal "Plan de packing" — muestra el plan multi-caja sugerido.
//
// Lista las propuestas como cards expandibles. Si hay items_no_asignados
// o advertencias, sección amarilla al final con detalle accionable.
// Botón "Recalcular" llama a POST /pedidos/{id}/packing/recalcular.

import { apiGet, apiPost } from '../api/client.js';
import { icon } from './icons.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

let _host = null;
function _close() { if (_host) { _host.remove(); _host = null; } }

const formatPeso = (g) => g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${g} g`;

/** Color HSL determinístico por producto_id — mismo producto, mismo color
 *  en todas las cajas. Golden-angle rotation para evitar tonos parecidos. */
function colorForProducto(pid) {
  const hue = (pid * 137.508) % 360;
  return `hsl(${hue.toFixed(1)}, 55%, 65%)`;
}

/** Agrupa los placements de una caja en capas Z lógicas.
 *
 *  El algoritmo Extreme Point genera muchas z únicas (cada item al
 *  colocarse crea EPs en z = z_item + alto). Si los items tienen alturas
 *  variadas, terminas con micro-capas de 1-2cm que no son operativas.
 *
 *  Fusionamos capas cuyo `z` esté dentro de `LAYER_TOL_MM` del z inicial
 *  del grupo — así las que están "casi al mismo suelo" cuentan como una.
 *  Resultado: típicamente 2-3 capas físicas accionables por caja.
 */
const LAYER_TOL_MM = 30;
function calcularCapas(placements) {
  if (!placements.length) return [];
  const sorted = [...placements].sort((a, b) => a.z - b.z);
  const grupos = [];
  let actual = { zBase: sorted[0].z, items: [sorted[0]] };
  for (let i = 1; i < sorted.length; i++) {
    const p = sorted[i];
    if (p.z - actual.zBase <= LAYER_TOL_MM) {
      actual.items.push(p);
    } else {
      grupos.push(actual);
      actual = { zBase: p.z, items: [p] };
    }
  }
  grupos.push(actual);
  return grupos.map((g, i) => ({
    indice: i + 1,
    z: g.zBase,
    zTop: Math.max(...g.items.map(p => p.z + p.alto)),
    cantidad: g.items.length,
    items: g.items,
  }));
}

/** Pills de capas: "Todas | Capa 1 | Capa 2 …" — devuelve solo HTML. */
function capasPillsHTML(c, layerActiva) {
  const capas = calcularCapas(c.placements);
  if (capas.length <= 1) return '';  // No vale la pena para una sola capa.
  const pills = [
    `<button type="button" class="plan-capa-pill ${layerActiva === null ? 'active' : ''}"
       data-caja="${c.indice}" data-layer="all">Todas (${c.placements.length})</button>`,
    ...capas.map(cap => `
      <button type="button" class="plan-capa-pill ${layerActiva === cap.indice ? 'active' : ''}"
        data-caja="${c.indice}" data-layer="${cap.indice}"
        title="z=${cap.z} → ${cap.zTop}mm">
        Capa ${cap.indice} <small>(${cap.cantidad})</small>
      </button>`),
  ].join('');
  return `<div class="plan-capa-pills">${pills}</div>`;
}

/** SVG top-down. Si `layerActiva` es null → todas (apiladas bottom-up con
 *  opacidad creciente). Si es un número → solo esa capa, las demás ocultas. */
function placementSVG(c, layerActiva = null) {
  if (!c.placements || c.placements.length === 0) return '';
  const W = c.caja_interior_ancho_mm;
  const D = c.caja_interior_fondo_mm;
  if (!W || !D) return '';
  const capas = calcularCapas(c.placements);

  let toRender;
  let subLabel;
  if (layerActiva === null) {
    // Todas: ordenadas por z asc → las capas superiores se pintan encima.
    toRender = [...c.placements].sort((a, b) => a.z - b.z);
    subLabel = `${capas.length} ${capas.length === 1 ? 'capa' : 'capas'} · ${c.placements.length} items`;
  } else {
    const capa = capas.find(x => x.indice === layerActiva);
    if (!capa) return '';
    toRender = capa.items;
    subLabel = `Capa ${capa.indice} de ${capas.length} · z=${capa.z}→${capa.zTop} mm · ${capa.cantidad} items`;
  }

  // Índice de capa por placement — la tolerancia hace que p.z no sea el
  // identificador único de la capa; buscamos en qué grupo cae.
  const indicePorPlacement = new Map();
  for (const cap of capas) {
    for (const item of cap.items) indicePorPlacement.set(item, cap.indice - 1);
  }
  const items = toRender.map(p => {
    const color = colorForProducto(p.producto_id);
    // En modo "Todas": opacidad ligeramente menor para items de capas altas
    // para que el operario perciba el apilamiento sin perder de vista lo de abajo.
    const opacity = layerActiva === null
      ? Math.max(0.55, 0.95 - 0.12 * (indicePorPlacement.get(p) ?? 0))
      : 0.9;
    return `<g>
      <rect x="${p.x}" y="${p.y}" width="${p.ancho}" height="${p.fondo}"
            fill="${color}" stroke="rgba(0,0,0,0.35)" stroke-width="2"
            opacity="${opacity.toFixed(2)}"/>
      <title>${escapeHtml(p.descripcion)} · ${p.ancho}×${p.fondo}×${p.alto}mm @ (${p.x},${p.y},${p.z})</title>
    </g>`;
  }).join('');

  // Leyenda: en modo capa solo productos de esa capa; en "Todas" todos.
  const seen = new Map();
  for (const p of toRender) {
    if (!seen.has(p.producto_id)) seen.set(p.producto_id, p.descripcion);
  }
  const legend = Array.from(seen, ([pid, descr]) => `
    <span class="legend-chip">
      <span class="legend-swatch" style="background:${colorForProducto(pid)};"></span>
      ${escapeHtml(descr)}
    </span>`).join('');

  return `
    <div class="plan-canvas-wrap" data-caja="${c.indice}">
      ${capasPillsHTML(c, layerActiva)}
      <div class="plan-canvas-label">Vista cenital · ${W} × ${D} mm interior · ${subLabel}</div>
      <svg class="plan-canvas" viewBox="0 0 ${W} ${D}" preserveAspectRatio="xMidYMid meet">
        <rect x="0" y="0" width="${W}" height="${D}"
              fill="#f9fafb" stroke="#374151" stroke-width="4"/>
        ${items}
      </svg>
      <div class="plan-legend">${legend}</div>
    </div>`;
}

function cajaCardHTML(c) {
  const itemsList = c.items.map(it => `
    <li class="plan-item">
      <span><span class="legend-swatch" style="background:${colorForProducto(it.producto_id)};"></span>${escapeHtml(it.descripcion)}</span>
      <strong>×${it.cantidad}</strong>
    </li>`).join('');
  const colorPct = c.volumen_ocupado_pct >= 70
    ? '#15803d'
    : c.volumen_ocupado_pct >= 40
      ? '#475569'
      : '#b45309';
  return `
    <details class="plan-caja-card">
      <summary>
        <div class="plan-caja-head">
          <strong>Caja ${c.indice}: ${escapeHtml(c.caja_tipo_codigo)}</strong>
          <span class="plan-caja-sub">${escapeHtml(c.caja_tipo_nombre)}</span>
        </div>
        <div class="plan-caja-stats">
          <span>${c.items.reduce((a, i) => a + i.cantidad, 0)} items</span>
          <span>·</span>
          <span>${formatPeso(c.peso_estimado_g)}</span>
          <span>·</span>
          <span style="color:${colorPct};font-weight:600;">${c.volumen_ocupado_pct}%</span>
        </div>
      </summary>
      ${placementSVG(c)}
      <ul class="plan-items">${itemsList}</ul>
    </details>`;
}

function advertenciasHTML(plan) {
  if (plan.items_no_asignados.length === 0 && plan.advertencias.length === 0) return '';
  const itemsHTML = plan.items_no_asignados.map(it => `
    <li class="plan-item">
      <span>${escapeHtml(it.descripcion)}</span>
      <strong>×${it.cantidad}</strong>
    </li>`).join('');
  const adv = plan.advertencias.map(w => `<li>${escapeHtml(w)}</li>`).join('');
  return `
    <div class="plan-warnings">
      <div class="plan-warnings-head">
        ${icon('alert-triangle', { size: 20 })}
        <strong>Atención</strong>
      </div>
      ${adv ? `<ul class="plan-warnings-list">${adv}</ul>` : ''}
      ${itemsHTML ? `
        <div style="margin-top:0.5rem;">
          <div style="font-size:var(--fs-sm);color:var(--c-fg-muted);margin-bottom:0.25rem;">No incluidos en el plan:</div>
          <ul class="plan-items">${itemsHTML}</ul>
        </div>` : ''}
    </div>`;
}

/**
 * Abre el modal del plan. `onRecalcular(plan)` se llama tras recalcular
 * (el caller usa el plan nuevo para refrescar banner / estado).
 */
export function openPlanPackingModal({ plan, pedidoId, onPlanChange }) {
  return new Promise((resolve) => {
    _host = document.createElement('div');
    document.body.appendChild(_host);
    let _planActual = plan;

    // Delegación de eventos para pills de capa Z — atado una sola vez al
    // host. Sobrevive a re-renders del contenido sin duplicar listeners.
    _host.addEventListener('click', (ev) => {
      const btn = ev.target.closest('.plan-capa-pill');
      if (!btn || !_host.contains(btn)) return;
      ev.preventDefault();
      ev.stopPropagation();
      const cajaIdx = parseInt(btn.dataset.caja, 10);
      const layerStr = btn.dataset.layer;
      const layer = layerStr === 'all' ? null : parseInt(layerStr, 10);
      const caja = _planActual.propuestas.find(x => x.indice === cajaIdx);
      if (!caja) return;
      const card = btn.closest('details.plan-caja-card');
      const wrap = card?.querySelector('.plan-canvas-wrap');
      if (!wrap) return;
      if (card && !card.hasAttribute('open')) card.setAttribute('open', '');
      const tmp = document.createElement('div');
      tmp.innerHTML = placementSVG(caja, layer);
      const nuevoWrap = tmp.firstElementChild;
      if (nuevoWrap) wrap.replaceWith(nuevoWrap);
    }, { capture: true });

    const render = (p) => {
      _planActual = p;
      const cajasHTML = p.propuestas.length === 0
        ? `<p style="color:var(--c-fg-muted);text-align:center;padding:1rem 0;">No hay nada que empaquetar — todo está enviado o ya en caja.</p>`
        : p.propuestas.map(cajaCardHTML).join('');

      const modoActual = p.modo || 'compacto';
      const modoSelector = `
        <div class="plan-modo-selector">
          <span class="plan-modo-label">Modo:</span>
          <label class="plan-modo-opt ${modoActual === 'compacto' ? 'active' : ''}">
            <input type="radio" name="pp-modo" value="compacto" ${modoActual === 'compacto' ? 'checked' : ''}/>
            <span><strong>Compacto</strong><small>menos cajas, productos pueden repetirse</small></span>
          </label>
          <label class="plan-modo-opt ${modoActual === 'agrupado' ? 'active' : ''}">
            <input type="radio" name="pp-modo" value="agrupado" ${modoActual === 'agrupado' ? 'checked' : ''}/>
            <span><strong>Agrupado</strong><small>mismo producto junto, más cajas</small></span>
          </label>
        </div>`;

      _host.innerHTML = `
        <div class="scrim fade-in"><div class="modal" style="max-width: 560px;max-height:90dvh;overflow-y:auto;">
          <h2 style="margin-top:0;">
            ${icon('package-search', { size: 22 })}
            Plan de packing sugerido
          </h2>
          ${modoSelector}
          ${p.propuestas.length > 0 ? `
            <p style="color:var(--c-fg-muted);margin-top:0;font-size:var(--fs-sm);">
              ${p.propuestas.length} ${p.propuestas.length === 1 ? 'caja' : 'cajas'} ·
              ${resumen(p)}
            </p>` : ''}
          <div class="plan-cajas-list">${cajasHTML}</div>
          ${advertenciasHTML(p)}
          <p class="hint">Es una sugerencia orientativa. Puedes empaquetar como quieras — el sistema marcará cuánto del plan se está siguiendo.</p>
          <div class="actions">
            <button class="btn --ghost" id="pp-close">Cerrar</button>
            <button class="btn --primary" id="pp-recalc">${icon('rotate-cw', { size: 16 })} Recalcular</button>
          </div>
        </div></div>`;

      _host.querySelector('#pp-close').addEventListener('click', () => { _close(); resolve(p); });

      _host.querySelectorAll('input[name="pp-modo"]').forEach(input => {
        input.addEventListener('change', async (ev) => {
          const nuevoModo = ev.target.value;
          if (nuevoModo === p.modo) return;
          // Lo marcamos visualmente como "calculando" antes de refrescar.
          _host.querySelectorAll('.plan-modo-opt').forEach(o => o.classList.add('loading'));
          try {
            const fresh = await apiGet(`/pedidos/${pedidoId}/packing?modo=${nuevoModo}`);
            if (onPlanChange) onPlanChange(fresh);
            render(fresh);
          } catch (e) {
            _host.querySelectorAll('.plan-modo-opt').forEach(o => o.classList.remove('loading'));
            alert(`No se pudo cambiar el modo: ${e.message}`);
          }
        });
      });

      _host.querySelector('#pp-recalc').addEventListener('click', async (ev) => {
        const btn = ev.currentTarget;
        btn.disabled = true;
        btn.innerHTML = 'Calculando…';
        try {
          const fresh = await apiPost(`/pedidos/${pedidoId}/packing/recalcular?modo=${modoActual}`);
          if (onPlanChange) onPlanChange(fresh);
          render(fresh);
        } catch (e) {
          btn.innerHTML = `${icon('rotate-cw', { size: 16 })} Recalcular`;
          btn.disabled = false;
          alert(`No se pudo recalcular: ${e.message}`);
        }
      });

    };

    render(plan);
  });
}

function resumen(plan) {
  // "2 × M + 1 × L"
  const counter = {};
  for (const c of plan.propuestas) {
    counter[c.caja_tipo_codigo] = (counter[c.caja_tipo_codigo] || 0) + 1;
  }
  return Object.entries(counter)
    .map(([codigo, n]) => n === 1 ? codigo : `${n} × ${codigo}`)
    .join(' + ');
}

export function resumenPlanCorto(plan) {
  // Para el banner: "2 cajas L + 1 M"
  if (!plan || !plan.propuestas || plan.propuestas.length === 0) return null;
  const counter = {};
  for (const c of plan.propuestas) {
    counter[c.caja_tipo_codigo] = (counter[c.caja_tipo_codigo] || 0) + 1;
  }
  const parts = Object.entries(counter)
    .map(([codigo, n]) => `${n} ${codigo}`);
  return parts.join(' + ');
}
